import { FC, Ref, useContext, useRef } from "react";

import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { MoveCoursesToGroupingModal } from "./MoveCoursesToGroupingModal";

import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, type MenuChangeEvent, MenuInstance, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, ChevronDownIcon, CopySendArrowIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCoursesWithGrouping } from "store/selectors/section";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";

type AddCourseOptionMenuProps = {
  groupingId: string;
  className?: string;
};

export const AddCourseOptionMenu: FC<AddCourseOptionMenuProps> = ({ groupingId, className }) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const coursesWithGrouping = useAppSelector(selectCoursesWithGrouping);
  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    hoverableContext?.setShouldStayOpen?.(event.open);
  };

  const handleAddNewCourse = () => {
    showAddCourseModal(groupingId);
    menuRef.current?.closeMenu();
  };

  const handleMoveExistingCourses = () => {
    showConfirmationModal({
      title: t("home.courses.move_courses_to_grouping_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <MoveCoursesToGroupingModal
          courses={coursesWithGrouping}
          groupingId={groupingId}
          onClose={onClose}
        />
      ),
    });

    menuRef.current?.closeMenu();
  };

  const MenuButton = (
    <Button
      className={className}
      eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course to Folder`}
      kind="gray"
      size="s"
      title={t("common.add.course")}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <IconText iconEnd={<ChevronDownIcon />} text={t("common.add.course")} />
    </Button>
  );

  return (
    <Menu
      instanceRef={menuRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onMenuChange={onMenuChange}
    >
      <MenuItem onClick={handleAddNewCourse}>
        <IconText iconStart={<AddPlusIcon />} text={t("home.courses.add_course_heading")} />
      </MenuItem>

      <MenuItem onClick={handleMoveExistingCourses}>
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("sidebar_options_menu.move_existing_courses_here")}
        />
      </MenuItem>
    </Menu>
  );
};
